import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./components/AdminLayout";
import StatsGrid from "./dashboard/components/StatsGrid";
import RevenueChart from "./dashboard/components/RevenueChart";
import ServicesChart from "./dashboard/components/ServicesChart";
import TodaySchedule from "./dashboard/components/TodaySchedule";
import TopDoctors from "./dashboard/components/TopDoctors";
import RecentActivity from "./dashboard/components/RecentActivity";
import QuickActions from "./dashboard/components/QuickActions";
import NotificationsPanel from "./dashboard/components/NotificationsPanel";
import VisitorsChart from "./dashboard/components/VisitorsChart";
import { getDashboardStats, type DashboardData, type AdminStats } from "@/api/dashboard";
import { getSession, hasPermission, ROLE_DEFINITIONS, type AdminSession } from "@/hooks/useRBAC";

function WelcomeBanner({ session, roleDef, stats }: { session: AdminSession; roleDef: { label: string; icon: string; color: string }; stats: AdminStats }) {
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "صباح الخير" : hour < 17 ? "مساء الخير" : "مساء النور";

  return (
    <div className="relative overflow-hidden bg-[#2E4E45] rounded-2xl p-6 text-white">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-64 h-64 rounded-full bg-white/5 -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-48 h-48 rounded-full bg-[#C8A96E]/20 translate-x-1/4 translate-y-1/4" />

      <div className="relative flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="relative">
            <img
              src={session.avatar}
              alt={session.name}
              className="w-14 h-14 rounded-2xl object-cover border-2 border-white/20"
            />
            <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-emerald-400 border-2 border-[#2E4E45]" />
          </div>
          <div>
            <p className="text-white/60 text-sm">{greeting}،</p>
            <h2 className="text-xl font-bold">{session.name}</h2>
            <span className={`inline-flex items-center gap-1.5 mt-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${roleDef.color}`}>
              <i className={`${roleDef.icon} text-xs`} />
              {roleDef.label}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Quick stats in banner */}
          <div className="text-center px-4 py-2 bg-white/10 rounded-xl">
            <p className="text-xl font-bold">{stats.todayBookings}</p>
            <p className="text-[10px] text-white/60">حجز اليوم</p>
          </div>
          <div className="text-center px-4 py-2 bg-white/10 rounded-xl">
            <p className="text-xl font-bold">{stats.pendingBookings}</p>
            <p className="text-[10px] text-white/60">معلق</p>
          </div>
          <div className="text-center px-4 py-2 bg-[#C8A96E]/30 rounded-xl">
            <p className="text-xl font-bold">{stats.unreadMessages}</p>
            <p className="text-[10px] text-white/60">رسائل جديدة</p>
          </div>
        </div>
      </div>

      {/* Date */}
      <div className="relative mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
        <p className="text-white/50 text-xs">
          {new Date().toLocaleDateString("ar-SA", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
        </p>
        <div className="flex items-center gap-1.5 text-xs text-white/50">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
          النظام يعمل بشكل طبيعي
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [session, setSession] = useState<AdminSession | null>(null);
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const s = getSession();
    if (!s) { navigate("/admin/login"); return; }
    setSession(s);

    const token = localStorage.getItem("access_token") || localStorage.getItem("admin_token") || "";

    getDashboardStats(token)
      .then((res) => {
        setData(res);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError(err.message || "فشل في تحميل بيانات لوحة التحكم");
        setLoading(false);
      });
  }, [navigate]);

  if (!session) return null;

  const roleDef = ROLE_DEFINITIONS[session.role];
  const perm = (p: string) => hasPermission(session, p);

  return (
    <AdminLayout>
      <div className="space-y-5">
        {loading ? (
          <div className="min-h-[400px] flex flex-col items-center justify-center bg-white rounded-2xl border border-gray-100 p-8">
            <div className="w-12 h-12 rounded-full border-4 border-[#2E4E45]/10 border-t-[#2E4E45] animate-spin mb-4" />
            <p className="text-sm text-gray-500 font-medium animate-pulse">جاري تحميل بيانات لوحة التحكم من الخادم...</p>
          </div>
        ) : error ? (
          <div className="min-h-[400px] flex flex-col items-center justify-center bg-white rounded-2xl border border-red-100 p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center text-red-500 mb-4">
              <i className="ri-error-warning-line text-2xl" />
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-1">عذراً، فشل تحميل البيانات</h3>
            <p className="text-sm text-gray-500 max-w-md mb-6">{error}</p>
            <button
              onClick={() => {
                setLoading(true);
                setError(null);
                const token = localStorage.getItem("access_token") || localStorage.getItem("admin_token") || "";
                getDashboardStats(token)
                  .then(setData)
                  .catch((err) => setError(err.message || "فشل في تحميل بيانات لوحة التحكم"))
                  .finally(() => setLoading(false));
              }}
              className="px-4 py-2 bg-[#2E4E45] text-white rounded-lg text-sm font-medium hover:bg-[#243d36] transition-colors cursor-pointer"
            >
              إعادة المحاولة
            </button>
          </div>
        ) : data ? (
          <>
            {/* Welcome Banner */}
            <WelcomeBanner session={session} roleDef={roleDef} stats={data.adminStats} />

            {/* Quick Actions */}
            <QuickActions onNavigate={navigate} hasPermission={perm} />

            {/* Stats Grid */}
            <StatsGrid stats={data.adminStats} onNavigate={navigate} />

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <div className="lg:col-span-2">
                <RevenueChart bookingsByMonth={data.chartData.bookingsByMonth} />
              </div>
              <div>
                <ServicesChart data={data.chartData.bookingsByService} />
              </div>
            </div>

            {/* Middle Row: Schedule + Doctors + Notifications */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <div className="lg:col-span-1">
                <TodaySchedule schedule={data.todaySchedule} />
              </div>
              <div className="lg:col-span-1">
                <TopDoctors doctors={data.topDoctors} onNavigate={navigate} />
              </div>
              <div className="lg:col-span-1">
                <NotificationsPanel />
              </div>
            </div>

            {/* Bottom Row: Activity + Visitors */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <div className="lg:col-span-2">
                <RecentActivity bookings={data.recentBookings} messages={data.recentMessages} />
              </div>
              <div className="lg:col-span-1">
                <VisitorsChart data={data.chartData.visitorsByDay} />
              </div>
            </div>
          </>
        ) : null}
      </div>
    </AdminLayout>
  );
}

